import { useState, useEffect } from 'react';
import { fetchPatientById, deletePatient } from '../services/api';
import './DeletePatientModal.css';

const fmt = (n) => '$' + (parseFloat(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Confirmation modal for archiving (soft-deleting) a patient.
 * Shows a danger warning with a brief financial summary and requires a free-text
 * reason before the biller can confirm.
 *
 * Props:
 *   patientId   – patient to archive (required)
 *   patientName – display name for the warning (required)
 *   financials  – optional pre-loaded { total_claims, collected, patient_paid,
 *                 patient_outstanding, denied }. When omitted, the modal fetches it.
 *   onClose     – called to dismiss without deleting
 *   onDeleted   – called after a successful archive
 */
export default function DeletePatientModal({ patientId, patientName, financials: financialsProp, onClose, onDeleted }) {
  const [financials, setFinancials] = useState(financialsProp || null);
  const [loadingFin, setLoadingFin] = useState(!financialsProp);
  const [reason, setReason] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  // Pull the financial summary when the caller didn't already have it loaded
  // (e.g. opening from the Patients list, where rows carry no financials).
  useEffect(() => {
    if (financialsProp) return;
    let cancelled = false;
    setLoadingFin(true);
    fetchPatientById(patientId)
      .then((res) => { if (!cancelled) setFinancials(res.data?.financials || null); })
      .catch(() => { if (!cancelled) setFinancials(null); })
      .finally(() => { if (!cancelled) setLoadingFin(false); });
    return () => { cancelled = true; };
  }, [patientId, financialsProp]);

  const collected   = financials ? (Number(financials.collected) || 0) + (Number(financials.patient_paid) || 0) : 0;
  const outstanding = Number(financials?.patient_outstanding) || 0;
  const denied      = Number(financials?.denied) || 0;
  const claimCount  = Number(financials?.total_claims) || 0;

  const bullets = [];
  if (claimCount > 0)  bullets.push({ key: 'claims', label: `${claimCount} claim${claimCount === 1 ? '' : 's'} on file`, danger: false });
  if (collected > 0)   bullets.push({ key: 'collected', label: `${fmt(collected)} collected to date`, danger: false });
  if (outstanding > 0) bullets.push({ key: 'outstanding', label: `${fmt(outstanding)} patient balance still outstanding`, danger: true });
  if (denied > 0)      bullets.push({ key: 'denied', label: `${fmt(denied)} in denied charges`, danger: false });

  const handleConfirm = async () => {
    if (!reason.trim()) { setError('Please enter a reason for deleting this patient.'); return; }
    setDeleting(true);
    setError(null);
    try {
      await deletePatient(patientId, reason.trim());
      onDeleted?.();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to delete patient.');
      setDeleting(false);
    }
  };

  return (
    <div className="dpm-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget && !deleting) onClose(); }}>
      <div className="dpm-modal" role="dialog" aria-modal="true">
        {/* Header */}
        <div className="dpm-header">
          <div className="dpm-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>
          <div className="dpm-header-text">
            <h2 className="dpm-title">Delete Patient</h2>
            <p className="dpm-subtitle">This will archive <strong>{patientName}</strong></p>
          </div>
          <button className="dpm-close" onClick={onClose} disabled={deleting} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Warning */}
        <div className="dpm-warning">
          <p className="dpm-warning-text">
            Deleting archives the patient and removes them from the patient list, search
            and payer lookups. Their claims and billing history are preserved and the
            patient can be restored later.
          </p>

          {loadingFin ? (
            <div className="dpm-fin-loading">Checking claims and balances…</div>
          ) : bullets.length > 0 ? (
            <>
              <p className="dpm-fin-heading">This patient still has:</p>
              <ul className="dpm-fin-list">
                {bullets.map((b) => (
                  <li key={b.key} className={b.danger ? 'dpm-fin-danger' : ''}>{b.label}</li>
                ))}
              </ul>
            </>
          ) : (
            <p className="dpm-fin-none">No claims or balances are on file for this patient.</p>
          )}
        </div>

        {/* Reason */}
        <div className="dpm-field">
          <label className="dpm-label">Reason for deletion <span className="dpm-required">*</span></label>
          <textarea
            className="dpm-textarea"
            rows="3"
            placeholder="e.g. Duplicate record, created in error, patient request…"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={deleting}
            autoFocus
          />
        </div>

        {error && <div className="dpm-error">{error}</div>}

        {/* Actions */}
        <div className="dpm-actions">
          <button className="dpm-btn-cancel" onClick={onClose} disabled={deleting}>Cancel</button>
          <button className="dpm-btn-delete" onClick={handleConfirm} disabled={deleting || !reason.trim()}>
            {deleting ? (
              <><span className="dpm-spinner" />Deleting…</>
            ) : (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
                Delete Patient
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
